/**
 * ExpandedParlayView Component
 * Detailed view of a tracked parlay with game matchups, quarter scores, and leg details
 */

import SportsbookBadge from '@components/ui/SportsbookBadge';
import { PARLAY_TIERS, type TierConfig } from '@config/constants';
import { useParlayScoring } from '@hooks/useParlayScoring';
import type {
  PredictionStats,
  ScoringParlay,
  ScoringResult,
  StrategyStats,
} from '@hooks/useStrategyStats';
import type { TrackedParlay } from '@hooks/useTrackedParlays';
import { ORIGIN_BADGES } from '@parlay-picker/shared/badgeConfig';
import { formatTrackedDate } from '@parlay-picker/shared/formatting';
import type { LiveGameScores, LiveLegStats, QuarterScore } from '@parlay-picker/shared/hooks';
import { LEAGUE_HELD_NO_SIGNAL_DESCRIPTION } from '@parlay-picker/shared/mlRecommendation';
import { parseReasoningWithRemovedLegs } from '@parlay-picker/shared/removedLegsUtils';
import { getParlayLabel, normalizeScoreForDisplay } from '@parlay-picker/shared/scoreDisplay';
import { getEffectiveStatType } from '@parlay-picker/shared/statType';
import { getLegAwayHome } from '@parlay-picker/shared/teamParsing';
import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import { cn } from '@utils/cn';
import { calculateEffectiveOdds, countDnpLegs, TIER_ICONS } from '@views/parlays/utils';
import { motion } from 'framer-motion';
import { ChevronUp, X } from 'lucide-react';
import React from 'react';
import { ExpandedLegCard } from './ExpandedLegCard';
import { GameMatchupHeader } from './GameMatchupHeader';

// =============================================================================
// Types
// =============================================================================

interface ExpandedParlayViewProps {
  parlay: TrackedParlay;
  onClose: () => void;
  onRemove?: (id: number) => void;
  strategyStats?: StrategyStats | null;
  predictionStats?: PredictionStats | null;
  /** Pre-computed scores from batch scoring (avoids per-parlay API call) */
  precomputedScores?: ScoringResult | null;
  getPlayerLiveStats: (
    gameId: string | null,
    playerId: number | null,
    playerName: string | null,
    statType: string,
  ) => LiveLegStats | null;
  getGameScores: (gameId: string | null) => LiveGameScores | null;
  getQuarterScores: (gameId: string | null) => QuarterScore[] | null;
  isGameLive: (gameId: string | null) => boolean;
  hasLiveData: (gameId: string | null) => boolean;
  canRemoveLeg: (gameId: string | null) => boolean;
  isPlayerDNP: (
    gameId: string | null,
    playerId: number | null,
    playerName: string | null,
  ) => boolean | null;
  isConnected: boolean;
}

// =============================================================================
// Styles
// =============================================================================

// Fixed brand colors for risk tiers — matches mobile RISK_TIER palette.
// Theme semantic colors (primary, warning) collide in some themes.
const TIER_STYLES: Record<string, string> = {
  'Safe Play': 'bg-teal-500/15 text-teal-400 border-teal-500',
  'Value Play': 'bg-indigo-500/15 text-indigo-400 border-indigo-500',
  'Upside Play': 'bg-orange-500/15 text-orange-400 border-orange-500',
  'Long Shot': 'bg-red-500/15 text-red-400 border-red-500',
  Custom: 'bg-theme-surface-hover/50 text-theme-text-secondary border-theme-border',
  'AI Improved': 'bg-cyan-500/15 text-cyan-400 border-cyan-700',
};

const RESULT_STYLES: Record<string, string> = {
  won: 'bg-theme-success/30 text-theme-success',
  lost: 'bg-theme-danger/30 text-theme-danger',
  void: 'bg-purple-500/15 text-purple-400',
  pending: 'bg-theme-warning/15 text-theme-warning',
  push: 'bg-theme-surface-hover text-theme-text-secondary',
};

// =============================================================================
// Sub-components
// =============================================================================

interface TierBadgeProps {
  tier: string;
}

function TierBadge({ tier }: TierBadgeProps): React.ReactElement {
  const tierInfo: TierConfig | undefined = PARLAY_TIERS[tier];
  const style = TIER_STYLES[tier] || TIER_STYLES.Custom;
  const IconComponent = tierInfo ? TIER_ICONS[tierInfo.icon] : undefined;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${style}`}
      title={tierInfo?.description}
    >
      {IconComponent && <IconComponent className="w-3 h-3" />} {tier}
    </span>
  );
}

interface ParlayResultBadgeProps {
  result?: string;
}

function ParlayResultBadge({ result }: ParlayResultBadgeProps): React.ReactElement {
  const style = RESULT_STYLES[result || ''] || RESULT_STYLES.pending;
  const label = result?.toUpperCase() || 'PENDING';
  return <span className={`px-2 py-0.5 rounded text-xs font-bold ${style}`}>{label}</span>;
}

interface OriginBadgeProps {
  origin: 'ai' | 'manual' | 'bet';
}

function OriginBadge({ origin }: OriginBadgeProps): React.ReactElement | null {
  const badge = ORIGIN_BADGES[origin];
  if (!badge) return null;

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
      {badge.label}
    </span>
  );
}

interface ScoreBadgeProps {
  score: number;
  label: string;
  icon: string;
  tooltip: string;
  /** Raw thresholds for normalization */
  thresholds?: { bet: number; skip: number };
  /** False greys out a BET or LEAN score; absent keeps its colour. */
  isLabelTrusted?: boolean;
}

function ScoreBadge({
  score,
  label,
  icon,
  tooltip,
  thresholds,
  isLabelTrusted,
}: ScoreBadgeProps): React.ReactElement {
  const displayScore = normalizeScoreForDisplay(score, thresholds);
  const { label: displayRec, color } = getParlayLabel(displayScore, isLabelTrusted);
  const colorClass =
    color === 'success'
      ? 'bg-theme-success/15 text-theme-success border-theme-success'
      : color === 'lean'
        ? 'bg-teal-500/15 text-teal-400 border-teal-400'
        : color === 'danger'
          ? 'bg-theme-danger/15 text-theme-danger border-theme-danger'
          : color === 'none'
            ? 'bg-slate-500/15 text-slate-400 border-slate-400'
            : 'bg-theme-warning/15 text-theme-warning border-theme-warning';

  return (
    <span
      className={`text-xs px-2 py-1 rounded border ${colorClass} cursor-help`}
      title={
        displayRec === 'NO SIGNAL' ? `${tooltip}. ${LEAGUE_HELD_NO_SIGNAL_DESCRIPTION}` : tooltip
      }
    >
      {icon} {label}: {displayScore}
    </span>
  );
}

// =============================================================================
// Component
// =============================================================================

export function ExpandedParlayView({
  parlay,
  onClose,
  onRemove,
  strategyStats,
  predictionStats,
  precomputedScores,
  getPlayerLiveStats,
  getGameScores,
  getQuarterScores,
  isGameLive,
  hasLiveData,
  canRemoveLeg,
  isPlayerDNP,
  isConnected,
}: ExpandedParlayViewProps): React.ReactElement {
  // Group legs by gameId for game matchup headers
  const legsByGame = React.useMemo(() => {
    const grouped = new Map<string, typeof parlay.legs>();
    for (const leg of parlay.legs) {
      const gameId = leg.gameId || 'unknown';
      const existing = grouped.get(gameId) || [];
      existing.push(leg);
      grouped.set(gameId, existing);
    }
    return grouped;
  }, [parlay.legs]);

  // Score this parlay via server-side scoring API (skip if precomputed scores provided)
  const { scores, loading: scoringLoading, scoreParlays } = useParlayScoring();

  const parlayForScoring: ScoringParlay = React.useMemo(
    () => ({
      name: parlay.tier || parlay.name || 'Custom',
      confidence: parlay.confidence || undefined,
      legs: parlay.legs.map((leg) => ({
        player: leg.player || undefined,
        team: leg.team || undefined,
        propType: leg.statType || undefined,
        statType: leg.statType || undefined,
        line: leg.line ?? undefined,
        overUnder: leg.overUnder || undefined,
        direction: leg.overUnder || undefined,
        edge: leg.aiEdge || undefined,
        opponent: leg.opponentTeam || undefined,
        isB2b: leg.isB2b ?? undefined,
        blowoutRisk: leg.blowoutRisk ?? undefined,
        gameId: leg.gameId ?? undefined,
        leagueKey: leg.leagueKey,
      })),
    }),
    [parlay],
  );

  React.useEffect(() => {
    // Skip API call when parent already provides batch-scored results
    if (precomputedScores !== undefined) return;
    scoreParlays([parlayForScoring]);
  }, [parlayForScoring, scoreParlays, precomputedScores]);

  // Use precomputed scores when available, otherwise fall back to hook's API scores
  const effectiveScores = precomputedScores !== undefined ? precomputedScores : (scores[0] ?? null);
  const betsScoreResult = effectiveScores?.strategyScore ?? null;
  const aiScoreResult = effectiveScores?.predictionScore ?? null;
  const isLoadingScores = precomputedScores !== undefined ? false : scoringLoading;

  // DNP-adjusted odds display
  const effectiveOdds = React.useMemo(
    () => calculateEffectiveOdds(parlay, isPlayerDNP),
    [parlay, isPlayerDNP],
  );
  const dnpCount = React.useMemo(() => countDnpLegs(parlay, isPlayerDNP), [parlay, isPlayerDNP]);
  // Prefer client-calculated effectiveOdds (handles old settled parlays where finalOdds == totalOdds)
  const displayOdds = effectiveOdds || parlay.finalOdds;
  const showOddsChange = displayOdds && displayOdds !== parlay.totalOdds;

  // Can remove if pending and all games haven't started
  const showRemoveButton =
    parlay.result === 'pending' &&
    onRemove &&
    parlay.legs.every((leg) => canRemoveLeg(leg.gameId ?? null));

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 } as const}
      animate={{ opacity: 1, scale: 1 } as const}
      transition={{ duration: 0.3 }}
      className="bg-theme-bg rounded-lg border border-theme-border overflow-hidden rebrand-glass"
    >
      {/* Combined Header - Tier, Result, Sportsbook on left; Odds, Legs, Date on right */}
      <div className="px-4 py-2 border-b border-theme-border/50 bg-theme-surface/50">
        {/* Row 1: Main info */}
        <div className="flex flex-wrap items-center justify-between gap-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <TierBadge tier={parlay.tier || 'Custom'} />
            <ParlayResultBadge result={parlay.result} />
            {parlay.origins.map((origin) => (
              <OriginBadge key={origin} origin={origin} />
            ))}
            {parlay.sportsbook && <SportsbookBadge sportsbook={parlay.sportsbook} size="sm" />}
          </div>
          <div className="flex items-center gap-2 text-sm flex-wrap">
            {showOddsChange ? (
              <span className="flex items-center gap-1.5">
                <span className="text-theme-text-tertiary line-through text-xs">
                  {parlay.totalOdds}
                </span>
                <span
                  className={cn(
                    'font-bold',
                    parlay.result === 'won'
                      ? 'text-theme-success'
                      : parlay.result === 'lost'
                        ? 'text-theme-danger'
                        : parlay.result === 'void' || parlay.result === 'push'
                          ? 'text-theme-warning'
                          : 'text-theme-text-secondary',
                  )}
                  title={`${dnpCount} leg${dnpCount !== 1 ? 's' : ''} voided (DNP)`}
                >
                  {displayOdds}
                </span>
              </span>
            ) : (
              <span
                className={cn(
                  'font-bold',
                  parlay.result === 'won'
                    ? 'text-theme-success'
                    : parlay.result === 'lost'
                      ? 'text-theme-danger'
                      : parlay.result === 'void' || parlay.result === 'push'
                        ? 'text-theme-warning'
                        : 'text-theme-text-secondary',
                )}
              >
                {parlay.totalOdds}
              </span>
            )}
            <span className="text-theme-text-tertiary">•</span>
            <span className="text-theme-text-secondary">{parlay.legs.length} Legs</span>
            {parlay.confidence && (
              <>
                <span className="text-theme-text-tertiary">•</span>
                <span className="text-theme-text-secondary">{parlay.confidence}</span>
              </>
            )}
            <span className="text-theme-text-tertiary">•</span>
            <span className="text-theme-text-tertiary text-xs">
              {formatTrackedDate(parlay.createdAt)}
            </span>
            {showRemoveButton && (
              <button
                type="button"
                onClick={() => {
                  onRemove!(parlay.id);
                  onClose();
                }}
                className="text-theme-danger hover:text-theme-danger/80 transition ml-1"
                title="Remove from tracking"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-theme-text-tertiary hover:text-theme-text transition ml-1"
              title="Collapse"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Row 2: Scoring badges (only show if we have scores) */}
        {(strategyStats || predictionStats) && (
          <div className="flex items-center gap-2 mt-1.5">
            {isLoadingScores ? (
              <span className="text-xs text-theme-text-secondary animate-pulse">Scoring...</span>
            ) : (
              <>
                {strategyStats && betsScoreResult && (
                  <ScoreBadge
                    score={betsScoreResult.score}
                    label="You"
                    icon=""
                    tooltip="Your personal score"
                    isLabelTrusted={betsScoreResult.isLabelTrusted}
                  />
                )}
                {predictionStats && aiScoreResult && (
                  <ScoreBadge
                    score={aiScoreResult.score}
                    label="AI"
                    icon=""
                    tooltip="Score based on all AI prediction outcomes"
                    isLabelTrusted={aiScoreResult.isLabelTrusted}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Game matchups with legs */}
      <div className="p-3 space-y-3 max-h-[60vh] overflow-y-auto">
        {Array.from(legsByGame.entries()).map(([gameId, legs]) => {
          // Get the first leg to extract game info
          const firstLeg = legs[0];
          const quarterScores = getQuarterScores(gameId);
          const gameScores = getGameScores(gameId);
          // The leg's own teams, for a box score opened while live scores carry none
          const legMatchup = getLegAwayHome(firstLeg);
          const gameLive = isGameLive(gameId);
          const hasData = hasLiveData(gameId);

          return (
            <div key={gameId} className="space-y-2">
              {/* Game matchup header */}
              <GameMatchupHeader
                leg={firstLeg}
                quarterScores={quarterScores}
                gameScores={gameScores}
                isLive={gameLive}
                hasLiveData={hasData}
                gameDate={firstLeg.gameDate}
                onPress={() =>
                  useGlobalShortcutsStore
                    .getState()
                    .openBoxScore(
                      gameId,
                      legs.map((l) => l.player).filter(Boolean) as string[],
                      firstLeg.leagueKey,
                      {
                        homeTeam: gameScores?.homeTeam ?? legMatchup?.home,
                        awayTeam: gameScores?.awayTeam ?? legMatchup?.away,
                      },
                    )
                }
              />

              {/* Legs for this game */}
              <div className="space-y-2 ml-2">
                {legs.map((leg, idx) => {
                  // Check for live DNP (player not in box score for live/final game)
                  const liveDnp = leg.player
                    ? isPlayerDNP(leg.gameId ?? null, leg.playerId ?? null, leg.player) === true
                    : false;
                  return (
                    <ExpandedLegCard
                      key={idx}
                      leg={leg}
                      legIndex={idx}
                      liveStats={getPlayerLiveStats(
                        leg.gameId ?? null,
                        leg.playerId ?? null,
                        leg.player ?? null,
                        getEffectiveStatType(leg),
                      )}
                      gameScores={getGameScores(leg.gameId ?? null)}
                      isLive={isGameLive(leg.gameId ?? null)}
                      hasLiveData={hasLiveData(leg.gameId ?? null)}
                      isConnected={isConnected}
                      liveDnp={liveDnp}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer - only show if there's AI reasoning */}
      {parlay.reasoning &&
        (() => {
          const { mainReasoning, removedLegsNote } = parseReasoningWithRemovedLegs(
            parlay.reasoning,
          );
          return (
            <div className="px-4 py-2 border-t border-theme-border bg-theme-surface/30">
              <div className="text-xs text-theme-text-tertiary mb-1">AI Reasoning</div>
              <p className="text-xs text-theme-text-secondary italic leading-relaxed">
                {mainReasoning}
              </p>
              {removedLegsNote && (
                <p className="text-yellow-500 text-xs mt-1">Note: {removedLegsNote}</p>
              )}
            </div>
          );
        })()}
    </motion.div>
  );
}
